import React, { useState, useEffect, useMemo } from 'react';
import Modal from 'aldoo-ra/Modal';
import Typography from 'aldoo-ra/Typography';
import Input from 'aldoo-ra/Input';
import { showNotification } from 'aldoo-ra/Notification';

/**
 * Global Environment Dialog - displays global environment variables
 */
export default function GlobalEnvDialog({ isOpen, onClose, controller }) {
    const [globalEnvEntries, setGlobalEnvEntries] = useState([]);
    const [globalEnvSearch, setGlobalEnvSearch] = useState('');

    useEffect(() => {
        if (isOpen) {
            loadGlobalEnv();
        }
    }, [isOpen]);

    // Auto-refresh global environment when dialog is open
    useEffect(() => {
        if (!isOpen) return;

        const interval = setInterval(() => {
            loadGlobalEnv();
        }, 3000); // Refresh every 3 seconds

        return () => clearInterval(interval);
    }, [isOpen]);

    const loadGlobalEnv = async () => {
        try {
            const entries = await controller.getGlobalEnv();
            setGlobalEnvEntries(entries);
        } catch (error) {
            console.error('Failed to load global environment:', error);
            showNotification({
                message: 'Failed to load global environment',
                type: 'error'
            });
        }
    };

    const filteredGlobalEnv = useMemo(() => {
        if (!globalEnvSearch) return globalEnvEntries;
        const query = globalEnvSearch.toLowerCase();
        return globalEnvEntries.filter(entry =>
            entry.key.toLowerCase().includes(query) ||
            entry.value.toLowerCase().includes(query)
        );
    }, [globalEnvEntries, globalEnvSearch]);

    const handleClose = () => {
        setGlobalEnvSearch('');
        onClose();
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={
                <div className="flex items-center gap-2">
                    <span>Global Environment</span>
                    <div className="flex items-center gap-1">
                        <div className="w-2 h-2 rounded-full bg-admin_success animate-pulse"></div>
                        <Typography variant="admin_body" className="text-xs text-admin_text_secondary">
                            Live
                        </Typography>
                    </div>
                </div>
            }
            variant="admin_centered"
            styleOverrides={{
                container: "w-full max-w-4xl max-h-full md:max-h-[calc(100vh-4rem)] bg-admin_paper overflow-y-auto scrollbar-thin scrollbar-thumb-admin_neutral_2 scrollbar-track-admin_paper md:rounded-lg border border-admin_bordered shadow-xl mx-4"
            }}
        >
            <div className="p-6 space-y-4">
                <Input
                    variant="admin"
                    placeholder="Search keys or values..."
                    value={globalEnvSearch}
                    onChange={(e) => setGlobalEnvSearch(e.target.value)}
                    showClearButton={true}
                />

                <div className="border border-admin_bordered rounded-md overflow-hidden">
                    <table className="w-full">
                        <thead className="bg-admin_neutral_1">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-admin_text_secondary uppercase">Key</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-admin_text_secondary uppercase">Value</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-admin_text_secondary uppercase w-20">Type</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-admin_bordered">
                            {filteredGlobalEnv.length > 0 ? (
                                filteredGlobalEnv.map((entry, index) => (
                                    <tr key={index} className="bg-admin_canvas hover:bg-admin_neutral_1">
                                        <td className="px-4 py-3">
                                            <Typography variant="admin_body" className="font-mono text-sm text-admin_text">
                                                {entry.key}
                                            </Typography>
                                        </td>
                                        <td className="px-4 py-3">
                                            <Typography variant="admin_body" className="font-mono text-sm text-admin_text_secondary break-all">
                                                {entry.value}
                                            </Typography>
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className="text-xs px-2 py-1 bg-admin_neutral_2 text-admin_text_secondary rounded">
                                                {entry.type}
                                            </span>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="3" className="px-4 py-8 text-center">
                                        <Typography variant="admin_body" className="text-admin_text_secondary">
                                            {globalEnvSearch ? 'No entries found matching your search' : 'No global environment entries'}
                                        </Typography>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="px-3 py-2 bg-admin_neutral_1 rounded">
                    <Typography variant="admin_body" className="text-xs text-admin_text_secondary">
                        {filteredGlobalEnv.length} {filteredGlobalEnv.length === 1 ? 'entry' : 'entries'}
                        {globalEnvSearch && filteredGlobalEnv.length !== globalEnvEntries.length &&
                            ` (${globalEnvEntries.length} total)`
                        }
                    </Typography>
                </div>
            </div>
        </Modal>
    );
}
